<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AgenteIaLayout from '../components/AgenteIaLayout.vue';
import { estadoDaCaixa } from '../caixas';

const { t } = useI18n();
const route = useRoute();
const carregando = ref(true);
const erro = ref('');
const caixas = ref([]);
const personaId = ref(null);
const comChave = ref(true);
const trocando = ref(null);
const substituirDialog = ref(null);
const robo = ref(null);
const caixaParaSubstituir = ref(null);

const carregar = async () => {
  try {
    const [{ data: status }, { data }] = await Promise.all([
      AgenteIaAPI.status(),
      AgenteIaAPI.caixas(),
    ]);
    personaId.value = status.persona?.id;
    comChave.value = !!status.persona?.com_chave;
    caixas.value = data.payload;
  } catch (e) {
    erro.value = e?.response?.data?.error || t('AGENTE_IA.CAIXAS.ERRO');
  } finally {
    carregando.value = false;
  }
};

onMounted(carregar);

const ligarCaixa = async (caixa, ligar, substituirBot = false) => {
  trocando.value = caixa.id;
  try {
    const { data } = await AgenteIaAPI.ligarCaixa(
      caixa.id,
      ligar ? personaId.value : null,
      substituirBot
    );
    caixas.value = data.payload;
  } catch (e) {
    if (e?.response?.status === 409) {
      robo.value = e.response.data.robo;
      caixaParaSubstituir.value = caixa;
      substituirDialog.value.open();
    } else {
      useAlert(e?.response?.data?.error || t('AGENTE_IA.CAIXAS.ERRO'));
    }
  } finally {
    trocando.value = null;
  }
};

const alternar = caixa =>
  ligarCaixa(caixa, estadoDaCaixa(caixa, personaId.value) !== 'LIGADA');

const confirmarSubstituicao = async () => {
  substituirDialog.value.close();
  await ligarCaixa(caixaParaSubstituir.value, true, true);
};

// Portao do Flow: nesta caixa o Flow fala primeiro e entrega ao agente quando quiser.
const alternarPortao = async (caixa, portao) => {
  trocando.value = caixa.id;
  try {
    const { data } = await AgenteIaAPI.portaoDaCaixa(caixa.id, portao);
    caixas.value = data.payload;
  } catch (e) {
    useAlert(e?.response?.data?.error || t('AGENTE_IA.CAIXAS.ERRO'));
  } finally {
    trocando.value = null;
  }
};
</script>

<template>
  <AgenteIaLayout
    :title="t('AGENTE_IA.CAIXAS.TITULO')"
    :description="t('AGENTE_IA.CAIXAS.DESCRICAO')"
  >
    <p v-if="carregando" class="text-sm text-n-slate-11">
      {{ t('AGENTE_IA.CARREGANDO') }}
    </p>
    <p v-else-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</p>
    <p v-else-if="!caixas.length" class="text-sm text-n-slate-11">
      {{ t('AGENTE_IA.CAIXAS.VAZIO') }}
    </p>
    <template v-else>
      <div
        v-if="!comChave"
        class="flex flex-col gap-2 p-4 text-sm rounded-lg bg-n-amber-3 text-n-amber-11 sm:flex-row sm:items-center sm:justify-between"
      >
        <span>{{ t('AGENTE_IA.CAIXAS.SEM_CHAVE') }}</span>
        <router-link
          :to="{
            name: 'agente_ia_chaves',
            params: { accountId: route.params.accountId },
          }"
          class="text-sm font-medium underline shrink-0"
        >
          {{ t('AGENTE_IA.INICIO.SEM_CHAVE.ACAO') }}
        </router-link>
      </div>
      <ul class="flex flex-col gap-2">
        <li
          v-for="caixa in caixas"
          :key="caixa.id"
          class="flex flex-col gap-3 p-3 rounded-lg border border-n-weak"
        >
          <div class="flex items-center justify-between gap-4">
            <div class="flex flex-col gap-0.5">
              <span class="text-sm text-n-slate-12">{{ caixa.name }}</span>
              <span class="text-xs text-n-slate-11">
                {{
                  t(
                    `AGENTE_IA.CAIXAS.ESTADO.${estadoDaCaixa(caixa, personaId)}`
                  )
                }}
              </span>
            </div>
            <Button
              size="sm"
              :variant="
                estadoDaCaixa(caixa, personaId) === 'LIGADA'
                  ? 'outline'
                  : 'solid'
              "
              :is-loading="trocando === caixa.id"
              :disabled="
                !comChave && estadoDaCaixa(caixa, personaId) !== 'LIGADA'
              "
              :label="
                t(
                  estadoDaCaixa(caixa, personaId) === 'LIGADA'
                    ? 'AGENTE_IA.CAIXAS.DESLIGAR'
                    : 'AGENTE_IA.CAIXAS.LIGAR'
                )
              "
              @click="alternar(caixa)"
            />
          </div>
          <label
            v-if="estadoDaCaixa(caixa, personaId) === 'LIGADA'"
            class="flex items-start gap-2"
          >
            <Switch
              class="mt-0.5"
              :model-value="caixa.portao"
              :disabled="trocando === caixa.id"
              @update:model-value="portao => alternarPortao(caixa, portao)"
            />
            <span class="flex flex-col gap-0.5">
              <span class="text-sm text-n-slate-12">
                {{ t('AGENTE_IA.CAIXAS.PORTAO') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ t('AGENTE_IA.CAIXAS.PORTAO_DICA') }}
              </span>
            </span>
          </label>
        </li>
      </ul>
    </template>
    <Dialog
      ref="substituirDialog"
      type="alert"
      :title="t('AGENTE_IA.CAIXAS.SUBSTITUIR_BOT.TITULO')"
      :description="
        robo
          ? t('AGENTE_IA.CAIXAS.SUBSTITUIR_BOT.DESCRICAO', { robo })
          : t('AGENTE_IA.CAIXAS.SUBSTITUIR_BOT.DESCRICAO_SEM_NOME')
      "
      :confirm-button-label="t('AGENTE_IA.CAIXAS.SUBSTITUIR_BOT.CONFIRMAR')"
      @confirm="confirmarSubstituicao"
    />
  </AgenteIaLayout>
</template>
